import Modal from "@/components/common/ui/modal";
import { CREATE_SITE } from "@/gql/site";
import { IOrganization } from "@/types/organization";
import { useMutation } from "@apollo/client";
import {
  ICreateSiteMutation,
  ICreateSiteMutationVariables,
  IGetSiteMetaInfoQuery,
} from "@shared/common/types/api-private/generated/graphql";
import { getStringByteLength } from "@shared/common/utils/value";
import { Form, Input } from "antd";
import { ChangeEvent } from "react";
import toast, { useToasterStore } from "react-hot-toast";

interface IAddSiteModalProps {
  open: boolean;
  onClose: () => void;
  currentOrganization: IOrganization;
  reload: () => Promise<unknown>;
}

const LENGTH_LIMIT_MESSAGE =
  "사이트명은 최대 60byte(한글 30자)를 넘길 수 없습니다.";

const AddSiteModal: React.FC<IAddSiteModalProps> = ({
  open,
  onClose,
  currentOrganization,
  reload,
}) => {
  const { toasts } = useToasterStore();

  const [form] = Form.useForm<IGetSiteMetaInfoQuery["siteMetaInfo"]>();

  const name = Form.useWatch("name", form);

  const hasSameToastMessage = (message: string) => {
    return toasts.some((toast) => toast.message === message);
  };

  const [createSite, { loading: createLoading }] = useMutation<
    ICreateSiteMutation,
    ICreateSiteMutationVariables
  >(CREATE_SITE, {
    onError: (err) => {
      toast(
        err.message ||
          "사이트를 추가하지 못했습니다. 잠시 후에 다시 시도해주세요."
      );
    },
    onCompleted: async () => {
      await reload();
      toast("사이트를 추가했습니다.");
      onClose();
    },
  });

  const handleSiteNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    const targetEle = e.currentTarget;
    const targetValue = e.currentTarget.value;
    const lastSelectionEnd = targetEle.selectionEnd;
    if (/^\s+/.test(targetValue)) {
      targetEle.value = String(name);
      form.setFieldValue("name", name);
      if (lastSelectionEnd) {
        targetEle.selectionEnd = lastSelectionEnd - 1;
      }
      return;
    }

    if (/\s\s/.test(targetValue)) {
      targetEle.value = String(name);
      form.setFieldValue("name", name);
      if (lastSelectionEnd) {
        targetEle.selectionEnd = lastSelectionEnd - 1;
      }
      return;
    }

    if (getStringByteLength(targetValue) > 60) {
      if (!hasSameToastMessage(LENGTH_LIMIT_MESSAGE)) {
        toast(LENGTH_LIMIT_MESSAGE);
      }

      targetEle.value = String(name);
      form.setFieldValue("name", name);
      if (lastSelectionEnd) {
        targetEle.selectionEnd = lastSelectionEnd - 1;
      }
    }
  };

  const handleAddSite = async () => {
    const values: IGetSiteMetaInfoQuery["siteMetaInfo"] =
      await form.validateFields();
    await createSite({
      variables: {
        organizationId: currentOrganization.id,
        object: values,
      },
    });
  };

  return (
    <Modal
      open={open}
      title="사이트 추가"
      okText="추가"
      onOk={() => handleAddSite()}
      okLoading={createLoading}
      okDisabled={!name}
      onClose={() => onClose()}
    >
      <Form
        form={form}
        requiredMark={false}
        layout="vertical"
        initialValues={{ name: "" }}
      >
        <div className="flex flex-col gap-6">
          <div className="flex items-center justify-between gap-9">
            <span className="flex items-baseline gap-1.5 text-base text-neutral-600">
              사이트명<span className="text-error-300">*</span>
            </span>
            <Form.Item noStyle name="name">
              <Input
                placeholder="사이트명을 입력해주세요."
                className="text-base w-[304px] text-black"
                onChange={handleSiteNameChange}
              />
            </Form.Item>
          </div>

          <div className="flex items-center justify-between gap-9">
            <span className="flex items-baseline text-base text-neutral-600">
              도메인
            </span>
            <Form.Item name="url" noStyle>
              <Input
                placeholder="도메인을 입력해주세요."
                className="text-base w-[304px] text-black"
              />
            </Form.Item>
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default AddSiteModal;
